<template>
  <div class="fortune-wheel-stats">
    <HeadSection
      :breadcrumbs="breadcrumbs"
      :permissionOfSection="permissionOfSection"
    />
    <MainContainer
      :loading="userLoading"
      :permissionOfSection="permissionOfSection"
      :isBlockStyle="true"
    >
      <div class="content">
        <div v-if="haveSpins || isActiveWheel" class="dashboard">
          <ContentBlock style="width: 100%; max-width: 50%;">
            <template #title>
              <div class="d-flex justify-content-between">
                <div class="d-flex flex-column">
                  <span class="d-block font-weight-bold font-size-20">Spin statistic</span>
                  <span class="font-weight-bold" style="color: #999">Current launch</span>
                </div>
                <a-button
                  v-if="haveSpins"
                  type="default"
                  size="large"
                  class="mr-3"
                  :disabled="initialLoading || loading"
                  @click="toggleShowExportModal(true, 'spin_statistics')"
                >
                  <a-icon type="upload" />
                  Export
                </a-button>
              </div>
            </template>
            <template #content>
              <div class="spin-stats">
                <Diagram
                  v-if="dashboardData"
                  :items="diagramItems"
                  width="400px"
                  height="280px"
                  :emptyVariant="!haveDashboardDetailed"
                  :piePosition="[haveDashboardDetailed ? '70%' : '50%', '50%']"
                  :legendPosition="{ left: 0 }"
                  :emphasis="{
                    padding: [5, 5],
                    fontSize: '16',
                  }"
                />
                <div class="spin-stats__info">
                  <div class="spin-stats__block">
                    <span class="spin-stats__title font-size-18">Total spins</span>
                    <Skeleton v-if="initialLoading || !dashboardData" width="80px" height="16px" />
                    <span v-else>{{ dashboardData.total_spins_count }}</span>
                  </div>
                  <div class="spin-stats__line" />
                  <div class="spin-stats__block">
                    <span class="spin-stats__title">Launch date:</span>
                    <Skeleton v-if="initialLoading || !dashboardData" width="100px" height="16px" />
                    <span v-else>{{ dashboardStartDate }}</span>
                  </div>
                  <div class="spin-stats__block">
                    <span class="spin-stats__title">End date:</span>
                    <Skeleton v-if="initialLoading || !dashboardData" width="100px" height="16px" />
                    <span v-else>{{ dashboardEndDate }}</span>
                  </div>
                </div>
              </div>
            </template>
          </ContentBlock>
          <ContentBlock v-if="showBudgetControl" style="width: 100%; max-width: 50%;">
            <template #title>
              <div class="d-flex justify-content-between">
                <div class="d-flex flex-column">
                  <span class="d-block font-weight-bold font-size-20">Budget control</span>
                  <span class="font-weight-bold" style="color: #999">Current launch</span>
                </div>
                <a-button
                  v-if="haveSpins"
                  type="default"
                  size="large"
                  class="mr-3"
                  :disabled="initialLoading || loading"
                  @click="toggleShowExportModal(true, 'budget_control')"
                >
                  <a-icon type="upload" />
                  Export
                </a-button>
              </div>
            </template>
            <template #content>
              <div class="budget-control">
                <div class="budget-control__blocks">
                  <div class="budget-control__block">
                    <span class="budget-control__block__title">Budget limit</span>
                    <span class="budget-control__block__value">{{ dashboardData.budget }}</span>
                  </div>
                  <div class="budget-control__block">
                    <span class="budget-control__block__title">Spent budget</span>
                    <span class="budget-control__block__value">{{ dashboardData.spent }}</span>
                  </div>
                  <div class="budget-control__block">
                    <span class="budget-control__block__title">Remaining budget</span>
                    <span class="budget-control__block__value budget-control__block__value-green">{{ dashboardData.remaining }}</span>
                  </div>
                </div>
                <div class="d-flex flex-column" style="min-width: 300px;">
                  <a-progress :strokeWidth="20" :percent="dashboardData.remaining_percent" :show-info="false" />
                  <div class="d-flex align-items-center justify-content-end">
                    <span class="font-size-15">
                      {{ 100 - dashboardData.remaining_percent === 0 ? `Completed` : `${100 - dashboardData.remaining_percent}% budget left` }}
                    </span>
                  </div>
                </div>
              </div>
            </template>
          </ContentBlock>
        </div>
        <ContentBlock style="width: 100%;">
          <template #title>
              <div class="d-flex justify-content-between">
                <div class="d-flex flex-column">
                  <span class="d-block font-weight-bold font-size-20">Spin history</span>
                </div>
                <a-button
                  v-if="haveSpins"
                  type="default"
                  size="large"
                  class="mr-3"
                  :disabled="initialLoading || loading"
                  @click="toggleShowExportModal(true, 'spin_history')"
                >
                  <a-icon type="upload" />
                  Export
                </a-button>
              </div>
            </template>
            <template #content>
              <SpinHistoryTable :isAuthWheelType="isAuthWheelType" />
            </template>
        </ContentBlock>
      </div>
    </MainContainer>
    <ExportModal
      :show="showExportModal"
      :exportInfo="exportModalInfo"
      :exportModalTarget="exportModalTarget"
      :actionButton="{
        text: 'Export',
        type: 'primary',
      }"
      :loading="loading"
      @close="toggleShowExportModal(false, '')"
    />
  </div>
</template>

<script>
import apiClient from '@/services/axios'
import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'
import ContentBlock from '@/components/main/contentBlock'
import Skeleton from '@/components/custom/skeleton/Skeleton.vue'
import Diagram from '@/components/custom/diagram/Diagram.vue'
import SpinHistoryTable from '@/views/apps/fortune-wheel/components/SpinHistoryTable.vue'
import ExportModal from '@/views/apps/fortune-wheel/components/ExportModal.vue'
import { getCurrentWheel } from '@/views/apps/fortune-wheel/composables/useGetCurrentWheel.js'
import { getSectorTypeName } from '@/views/apps/fortune-wheel/composables/useGetSectorTypeName.js'
import { getSectorColor } from '@/views/apps/fortune-wheel/composables/useGetSectorColor.js'
import { UTCFormatInCurrentTimezone } from '@/views/apps/fortune-wheel/composables/useFormatDate.js'

const wheelApiUrl = process.env.VUE_APP_WHEEL_API_URL

export default {
  components: {
    HeadSection,
    MainContainer,
    ContentBlock,
    Skeleton,
    Diagram,
    SpinHistoryTable,
    ExportModal,
  },
  data: () => ({
    permissionOfSection: 'fortune_wheel',
    breadcrumbs: [{ name: 'Fortune wheel', link: '/fortune-wheel' }],
    currentWheel: null,
    dashboardData: null,
    exportModalTarget: '',
    showExportModal: false,
    loading: false,
    initialLoading: true,
  }),
  computed: {
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    haveDashboardDetailed() {
      return !!this.dashboardData?.detailed?.length
    },
    diagramItems() {
      if (!this.dashboardData || !this.haveDashboardDetailed) {
        return [
          {
            name: 'Empty',
            value: 100,
            items: [{ name: 'Empty', value: 0 }],
            itemStyle: { color: getSectorColor() },
          },
        ]
      }

      const total = this.dashboardData.total_spins_count
      if (!total) {
        return []
      }

      // 1. Сначала считаем точные проценты
      const rawPercentages = this.dashboardData.detailed.map(detail => ({
        detail,
        raw: (detail.count / total) * 100,
      }))

      // 2. Округляем до двух знаков после точки
      const roundedPercentages = rawPercentages.map(p => ({
        detail: p.detail,
        rounded: parseFloat(p.raw.toFixed(2)),
      }))

      // 3. Проверяем разницу с 100 и корректируем последний элемент
      const sumRounded = roundedPercentages.reduce((sum, p) => sum + p.rounded, 0)
      const diff = parseFloat((100 - sumRounded).toFixed(2))

      // Добавляем разницу к последнему элементу (чтобы сумма была ровно 100)
      if (roundedPercentages.length > 0) {
        roundedPercentages[roundedPercentages.length - 1].rounded += diff
      }

      // 4. Формируем финальный массив
      return roundedPercentages.map(p => {
        const sectorName = getSectorTypeName(p.detail.sector_type)
        return {
          name: sectorName,
          value: p.rounded,
          items: [{ name: sectorName, value: p.detail.count }],
          itemStyle: { color: getSectorColor(p.detail.sector_type) },
        }
      })
    },
    isActiveWheel() {
      return this.currentWheel?.status === 'active'
    },
    isAuthWheelType() {
      return this.currentWheel?.type === 'authorized'
    },
    haveSpins() {
      return this.dashboardData?.total_spins_count > 0
    },
    showBudgetControl() {
      if (this.initialLoading) {
        return false
      }
      if (!this.isActiveWheel && this.haveSpins && this.currentWheel.spin_budget_status) {
        return true
      }

      return this.currentWheel.spin_budget_status
    },
    dashboardStartDate() {
      if (!this.dashboardData || !this.dashboardData?.start_date) {
        return '-'
      }
      return this.dashboardData?.fact_start_at
        ? this.formatDate(this.dashboardData.fact_start_at)
        : this.formatDate(this.dashboardData.start_date)
    },
    dashboardEndDate() {
      if (!this.dashboardData || !this.dashboardData?.end_date) {
        return '-'
      }
      if (!this.isActiveWheel) {
        return this.dashboardData?.fact_end_at
          ? this.formatDate(this.dashboardData.fact_end_at)
          : this.formatDate(this.dashboardData.end_date)
      }
      return this.formatDate(this.dashboardData.end_date)
    },
    exportModalInfo() {
      switch (this.exportModalTarget) {
        case 'spin_statistics':
          return {
            title: 'Export Current Launch Statistics',
            default_text: 'Export all spins that resulted in Freebets during the current launch.<br><br> A file will be generated, and a download link will appear once it’s ready.',
          }
        case 'budget_control':
          return {
            title: 'Export Spins with Freebets',
            default_text: 'Export all spins from the current run.<br><br> A file will be generated, and a download link will appear once it’s ready.',
          }
        case 'spin_history':
          return {
            title: 'Export Spin History',
            default_text: 'A file will be generated, and a download link will appear once it’s ready.',
          }
        default:
          return {
            title: '',
            default_text: '',
          }
      }
    },
  },
  methods: {
    formatDate(date) {
      return UTCFormatInCurrentTimezone(date, 'YYYY-MM-DD HH:mm', 'DD.MM.YYYY HH:mm')
    },
    toggleShowExportModal(status = true, target = '') {
      this.exportModalTarget = target
      this.showExportModal = status
    },
    async getDashboardData() {
      try {
        const currentWheelId = this.$route.params.id
        const url = `${wheelApiUrl}/admin/logs/${currentWheelId}/dashboard`
        const response = await apiClient.get(url)
        if (!response?.data?.data) {
          return
        }
        this.dashboardData = response.data.data
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      }
    },
    async getWheel() {
      const wheelId = this.$route.params.id
      const data = await getCurrentWheel(wheelId)
      if (!data) {
        return
      }
      this.currentWheel = data
      this.breadcrumbs.push({ name: this.currentWheel.name, link: `/fortune-wheel/${this.currentWheel.id}` }, { name: 'Statistics', link: undefined })
    },
  },
  async mounted() {
    const requests = [this.getWheel(), this.getDashboardData()]
    try {
      this.initialLoading = true
      await Promise.all(requests)
    } finally {
      setTimeout(() => {
        this.initialLoading = false
      }, 300)
    }
  },
}
</script>

<style lang="scss">
.fortune-wheel-stats {
  .content {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  .dashboard {
    display: flex;
    justify-content: space-between;
    gap: 20px;
  }
  .spin-stats {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 20px;
    width: 100%;
    &__info {
      display: flex;
      flex-direction: column;
      align-self: flex-end;
      gap: 10px;
      height: fit-content;
      border-radius: 4px;
      background-color: rgba(153, 153, 153, 0.08);
      padding: 10px;
      min-width: 300px;
    }
    &__block {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
    }
    &__title {
      font-weight: bold;
      color: #999;
    }
    &__line {
      width: 100%;
      height: 1px;
      background-color: rgba(153, 153, 153, 0.5);
    }
  }
  .budget-control {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 40px;
    width: 100%;
    &__blocks {
      display: flex;
      gap: 20px;
      width: 100%;
    }
    &__block {
      display: flex;
      flex-direction: column;
      gap: 15px;
      flex: 1 1 auto;
      border: 1px solid #999;
      border-radius: 4px;
      padding: 15px;
      &__title {
        font-size: 18px;
        font-weight: bold;
      }
      &__value {
        font-weight: bold;
        font-size: 24px;
        color: #999;
      }
      &__value-green {
        color: #1aafa0;
      }
    }
  }
}
</style>
